import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './AuthContext'
import { API_BASE_URL } from '../utils/api'

type Language = 'en' | 'fr'

interface LanguageContextType {
  language: Language
  setLanguage: (language: Language) => Promise<void>
  isLoading: boolean
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

interface LanguageProviderProps {
  children: React.ReactNode
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation()
  const { user, updateUser, loading: authLoading } = useAuth()
  const [language, setLanguageState] = useState<Language>('en')
  const [isLoading, setIsLoading] = useState(false)

  // Initialize language from user preference or browser/localStorage
  useEffect(() => {
    // Don't initialize language until auth is done loading
    if (authLoading) return

    if (user?.language) {
      // Use user's saved language preference
      const userLang = user.language as Language
      setLanguageState(userLang)
      i18n.changeLanguage(userLang)
    } else {
      // Use browser/localStorage default or fallback to 'en'
      const browserLang = (i18n.language?.startsWith('fr') ? 'fr' : 'en') as Language
      setLanguageState(browserLang)
      i18n.changeLanguage(browserLang)
    }
  }, [user, i18n, authLoading])

  const setLanguage = async (newLanguage: Language) => {
    console.log('LanguageContext: setLanguage called with:', newLanguage)
    setIsLoading(true)
    try {
      // Update i18next
      console.log('LanguageContext: Changing i18n language to:', newLanguage)
      await i18n.changeLanguage(newLanguage)
      setLanguageState(newLanguage)
      console.log('LanguageContext: Language state updated to:', newLanguage)

      // If user is logged in, save to backend
      if (user) {
        console.log('LanguageContext: User found, saving to backend:', user.email)
        const token = localStorage.getItem('token')
        if (token) {
          const response = await fetch(`${API_BASE_URL}/api/auth/language`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ language: newLanguage })
          })

          if (response.ok) {
            console.log('LanguageContext: Successfully saved language to backend')
            // Update user context with new language
            updateUser({ ...user, language: newLanguage })
          } else {
            console.error('LanguageContext: Failed to save language preference to backend')
          }
        }
      } else {
        console.log('LanguageContext: No user found, skipping backend save')
      }
    } catch (error) {
      console.error('LanguageContext: Error changing language:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const value: LanguageContextType = {
    language,
    setLanguage,
    isLoading
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
} 